import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Text, Group, Badge, Button, Stack, Loader, Code, CopyButton, ActionIcon, Tooltip, Box, Table, TextInput, Checkbox } from '@mantine/core';
import { Check, Copy, Plus, ArrowsClockwise, MagnifyingGlass, WarningCircle } from '@phosphor-icons/react';
import api from '../utils/api';

const STATUS_COLOR = { enabled: 'green', removed: 'gray', hidden: 'yellow', unverified: 'yellow' };

const TYPE_KEY = {
  webpage: 'webpage',
  webpage_codeless: 'webpageCodeless',
  google_analytics_4_custom: 'ga4',
  google_analytics_4_purchase: 'ga4',
  upload_clicks: 'uploadClicks',
  upload_calls: 'uploadCalls',
  websphone: 'phone',
  ad_call_metrics: 'adCallMetrics',
  store_visits: 'storeVisits',
  unknown: 'automatic',
};

// Why a given conversion has no website snippet to install (it is tracked another way).
function noCodeReason(t, type) {
  const ty = type || '';
  if (ty === 'unknown') return t('conversions.tab.noCode.unknown');
  if (ty.startsWith('upload')) return t('conversions.tab.noCode.upload');
  if (ty.includes('call') || ty === 'websphone') return t('conversions.tab.noCode.call');
  if (ty === 'store_visits') return t('conversions.tab.noCode.storeVisits');
  return t('conversions.tab.noCode.default');
}

// Gdzie wkleić kod zdarzenia, zależnie od kategorii konwersji. Bez tego użytkownicy
// wklejali tylko tag główny i myśleli, że konwersje już działają.
function whereToPasteKey(category) {
  const c = (category || '').toLowerCase();
  if (c === 'phone_call_lead') return 'phone';
  if (c === 'page_view') return 'pageView';
  if (['purchase', 'add_to_cart', 'begin_checkout', 'subscribe_paid'].includes(c)) return 'purchase';
  if (['submit_lead_form', 'contact', 'request_quote', 'book_appointment', 'signup', 'qualified_lead'].includes(c)) return 'lead';
  return 'default';
}

const CHECK_COLOR = { ok: 'green', missing: 'red', warning: 'yellow', error: 'red' };
const ACTION_COLOR = { recording: 'green', installed_not_seen: 'yellow', missing: 'red' };

function CodeBlock({ label, code }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Group justify="space-between" mb={4}>
        <Text size="xs" fw={500}>{label}</Text>
        <CopyButton value={code}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? t('conversions.tab.copied') : t('conversions.tab.copy')} withArrow>
              <ActionIcon size="sm" variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Group>
      <Code block style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{code}</Code>
    </Box>
  );
}

/**
 * Account-level conversion actions + their official Google tracking snippets.
 * This is the "Konwersje" tab the conversion-setup flow points users to so they
 * can copy the tracking code for any (incl. already-existing) conversion.
 */
function VerifyPanel({ campaignId, onClose }) {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [thankYou, setThankYou] = useState('');
  const [toggling, setToggling] = useState({});
  const [error, setError] = useState('');

  const run = (refresh = false) => {
    setRunning(true);
    setError('');
    api.post('/ads/conversions/verify/', {
      campaign_id: campaignId,
      thank_you_url: thankYou.trim() || undefined,
      refresh,
    })
      .then(res => {
        if (res.data.status === 'error') setError(res.data.message || t('conversions.verify.failed'));
        else setResult(res.data);
      })
      .catch(() => setError(t('conversions.verify.failed')))
      .finally(() => setRunning(false));
  };
  useEffect(() => { run(false); }, []);

  const setSecondary = (a) => {
    setToggling(p => ({ ...p, [a.id]: true }));
    api.post(`/ads/conversions/${a.id}/primary/`, { primary: false, name: a.name, campaign_id: campaignId })
      .then(res => {
        if (res.data.status === 'success') run(true);
        else setError(res.data.message || t('conversions.verify.failed'));
      })
      .catch(() => setError(t('conversions.verify.failed')))
      .finally(() => setToggling(p => ({ ...p, [a.id]: false })));
  };

  const checkLabel = (s) => t(`conversions.verify.checkStatus.${s}`, { defaultValue: s });
  const actionLabel = (s) => t(`conversions.verify.actionStatus.${s}`, { defaultValue: s });

  return (
    <Paper withBorder p="sm" radius="md">
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Text size="sm" fw={600}>{t('conversions.verify.title')}</Text>
        <Button size="compact-xs" variant="subtle" color="gray" onClick={onClose}>{t('conversions.verify.close')}</Button>
      </Group>
      <Text size="xs" c="dimmed" mb="xs">{t('conversions.verify.intro')}</Text>
      <Group gap="xs" mb="sm" wrap="nowrap" align="flex-end">
        <TextInput size="xs" style={{ flex: 1 }} label={t('conversions.verify.thankYouLabel')}
          placeholder={t('conversions.verify.thankYouPlaceholder')} value={thankYou}
          onChange={(e) => setThankYou(e.currentTarget.value)} />
        <Button size="xs" color="brand" loading={running} onClick={() => run(true)}>{t('conversions.verify.rerun')}</Button>
      </Group>

      {running && !result && (
        <Group gap="xs"><Loader size="xs" color="brand" /><Text size="xs" c="dimmed">{t('conversions.verify.running')}</Text></Group>
      )}
      {error && <Text size="xs" c="red" mb="xs">{error}</Text>}

      {result && (
        <Stack gap="sm">
          {(result.errors || []).map((e, i) => (
            <Group key={i} gap={6} wrap="nowrap" align="flex-start">
              <WarningCircle size={14} color="var(--mantine-color-yellow-7)" style={{ flexShrink: 0, marginTop: 2 }} />
              <Text size="xs">{e}</Text>
            </Group>
          ))}
          <Table fz="xs" withTableBorder verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('conversions.verify.colCheck')}</Table.Th>
                <Table.Th w={150}>{t('conversions.verify.colStatus')}</Table.Th>
                <Table.Th>{t('conversions.verify.colDetails')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(result.checks || []).map(c => (
                <Table.Tr key={c.key}>
                  <Table.Td fw={500}>{c.label}</Table.Td>
                  <Table.Td><Badge size="xs" color={CHECK_COLOR[c.status] || 'gray'}>{checkLabel(c.status)}</Badge></Table.Td>
                  <Table.Td>{c.message}</Table.Td>
                </Table.Tr>
              ))}
              {(result.actions || []).map(a => (
                <Table.Tr key={a.id}>
                  <Table.Td fw={500}>{a.name}{a.primary ? '' : ` (${t('conversions.verify.secondary')})`}</Table.Td>
                  <Table.Td><Badge size="xs" color={ACTION_COLOR[a.status] || 'gray'}>{actionLabel(a.status)}</Badge></Table.Td>
                  <Table.Td>
                    {t('conversions.verify.counts', { d7: a.conversions_7d || 0, d30: a.conversions_30d || 0 })}
                    {a.last_conversion_date ? ` · ${t('conversions.verify.lastConversion', { date: a.last_conversion_date })}` : ''}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {(result.actions || []).filter(a => (a.steps || []).length || (a.warnings || []).length).map(a => (
            <Box key={a.id}>
              <Text size="xs" fw={600} mb={4}>{a.name}</Text>
              <Stack gap={4}>
                {(a.warnings || []).map((w, i) => (
                  <Group key={`w${i}`} gap={6} wrap="nowrap" align="flex-start">
                    <WarningCircle size={14} color="var(--mantine-color-yellow-7)" style={{ flexShrink: 0, marginTop: 2 }} />
                    <Text size="xs">{w}</Text>
                  </Group>
                ))}
                {a.category === 'page_view' && a.primary && (
                  <Group>
                    <Button size="compact-xs" variant="light" color="brand" loading={!!toggling[a.id]}
                      onClick={() => setSecondary(a)}>{t('conversions.verify.makeSecondary')}</Button>
                  </Group>
                )}
                {(a.steps || []).map((st, i) => <Text key={`s${i}`} size="xs">{st}</Text>)}
                {(a.snippets || []).map((sn, i) => <CodeBlock key={`c${i}`} label={sn.label} code={sn.code} />)}
              </Stack>
            </Box>
          ))}

          <Text size="11px" c="dimmed">
            {t('conversions.verify.checkedPages')}: {(result.pages || []).map(p => p.url).join(', ') || '-'}
            {result.checked_at ? ` · ${new Date(result.checked_at).toLocaleString()}` : ''}
            {result.cached ? ` · ${t('conversions.verify.cached')}` : ''}
          </Text>
        </Stack>
      )}
    </Paper>
  );
}

export default function ConversionsTab({ onSetup, campaignId }) {
  const { t } = useTranslation();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [verifying, setVerifying] = useState(false);
  const [leadSelection, setLeadSelection] = useState('auto');
  const [leadSaving, setLeadSaving] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [primaryBusy, setPrimaryBusy] = useState({});

  const STATUS_LABEL = {
    enabled: t('conversions.tab.status.enabled'),
    hidden: t('conversions.tab.status.hidden'),
    unverified: t('conversions.tab.status.unverified'),
    removed: t('conversions.tab.status.removed'),
  };
  const typeLabel = (type) => (TYPE_KEY[type] ? t(`conversions.tab.types.${TYPE_KEY[type]}`) : (type || '-'));

  const load = () => {
    setLoading(true);
    api.get('/ads/conversions/', { params: campaignId ? { campaign_id: campaignId } : {} })
      .then(res => {
        setActions(res.data.actions || []);
        setLeadSelection(res.data.lead_selection === 'custom' ? 'custom' : 'auto');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [campaignId]);

  const saveLeads = (payload) => {
    setLeadSaving(true);
    setLeadError('');
    return api.post('/ads/conversions/lead-actions/', { campaign_id: campaignId, ...payload })
      .then(res => {
        if (res.data.status === 'success') {
          setLeadSelection(res.data.lead_selection === 'custom' ? 'custom' : 'auto');
          return api.get('/ads/conversions/', { params: campaignId ? { campaign_id: campaignId } : {} })
            .then(r => setActions(r.data.actions || []));
        }
        setLeadError(res.data.message || t('conversions.tab.lead.saveFailed'));
        return null;
      })
      .catch(() => setLeadError(t('conversions.tab.lead.saveFailed')))
      .finally(() => setLeadSaving(false));
  };

  const toggleLead = (a, checked) => {
    const current = actions.filter(x => x.is_lead).map(x => String(x.id));
    const next = checked ? [...new Set([...current, String(a.id)])] : current.filter(id => id !== String(a.id));
    saveLeads({ action_ids: next });
  };

  const makePrimary = (a) => {
    setPrimaryBusy(p => ({ ...p, [a.id]: true }));
    setLeadError('');
    api.post(`/ads/conversions/${a.id}/primary/`, { primary: true, name: a.name, campaign_id: campaignId })
      .then(res => {
        if (res.data.status === 'success') {
          return api.get('/ads/conversions/', { params: campaignId ? { campaign_id: campaignId } : {} })
            .then(r => setActions(r.data.actions || []));
        }
        setLeadError(res.data.message || t('conversions.tab.lead.saveFailed'));
      })
      .catch(() => setLeadError(t('conversions.tab.lead.saveFailed')))
      .finally(() => setPrimaryBusy(p => ({ ...p, [a.id]: false })));
  };

  if (loading) {
    return <Group gap="xs"><Loader size="xs" color="brand" /><Text size="sm" c="dimmed">{t('conversions.tab.loading')}</Text></Group>;
  }

  const enabled = actions.filter(a => a.status === 'enabled');
  const others = actions.filter(a => a.status !== 'enabled');

  const Row = (a) => {
    const hasCode = !!(a.tag_snippet || a.event_snippet);
    return (
    <Paper key={a.id} withBorder p="sm" radius="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <div style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>{a.name}</Text>
          <Text size="11px" c="dimmed">{typeLabel(a.type)}{a.category ? ` · ${a.category}` : ''}</Text>
        </div>
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          {a.status === 'enabled' && (
            <Checkbox size="xs" color="brand" label={t('conversions.tab.lead.column')}
              checked={!!a.is_lead} disabled={leadSaving}
              onChange={(e) => toggleLead(a, e.currentTarget.checked)} />
          )}
          <Badge size="xs" color={STATUS_COLOR[a.status] || 'gray'}>{STATUS_LABEL[a.status] || a.status}</Badge>
          {hasCode && (
            <Button size="compact-xs" variant="light" color="brand"
              onClick={() => setExpanded(p => ({ ...p, [a.id]: !p[a.id] }))}>
              {expanded[a.id] ? t('conversions.tab.hideCode') : t('conversions.tab.trackingCode')}
            </Button>
          )}
        </Group>
      </Group>
      {a.is_lead && a.primary === false && (
        <Group gap={6} mt={6} wrap="nowrap" align="center">
          <WarningCircle size={14} color="var(--mantine-color-yellow-7)" style={{ flexShrink: 0 }} />
          <Text size="xs">{t('conversions.tab.lead.secondaryWarning')}</Text>
          <Button size="compact-xs" variant="light" color="brand" loading={!!primaryBusy[a.id]}
            onClick={() => makePrimary(a)}>{t('conversions.tab.lead.makePrimary')}</Button>
        </Group>
      )}
      {!hasCode && <Text size="11px" c="dimmed" mt={4}>{noCodeReason(t, a.type)}</Text>}
      {hasCode && expanded[a.id] && (
        <Stack gap="sm" mt="sm">
          {a.tag_snippet && <CodeBlock label={t('conversions.tab.tagLabel')} code={a.tag_snippet} />}
          {a.event_snippet && (
            <Box>
              <Text size="xs" fw={500} mb={2}>{t('conversions.tab.whereToPaste.title')}</Text>
              <Text size="xs" c="dimmed" mb={6}>{t(`conversions.tab.whereToPaste.${whereToPasteKey(a.category)}`)}</Text>
              <CodeBlock label={t('conversions.tab.eventLabel')} code={a.event_snippet} />
            </Box>
          )}
        </Stack>
      )}
    </Paper>
    );
  };

  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm" c="dimmed">{t('conversions.tab.header')}</Text>
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          <ActionIcon variant="subtle" color="gray" onClick={load} title={t('conversions.tab.refresh')}><ArrowsClockwise size={16} /></ActionIcon>
          {actions.length > 0 && (
            <Button size="xs" variant="light" color="brand" leftSection={<MagnifyingGlass size={14} />}
              onClick={() => setVerifying(true)}>{t('conversions.verify.button')}</Button>
          )}
          {onSetup && <Button size="xs" leftSection={<Plus size={14} />} color="brand" onClick={onSetup}>{t('conversions.tab.addConversion')}</Button>}
        </Group>
      </Group>
      {verifying && <VerifyPanel campaignId={campaignId} onClose={() => setVerifying(false)} />}
      {actions.length > 0 && (
        <Group justify="space-between" wrap="nowrap" align="center">
          <Text size="xs" c="dimmed">
            {leadSelection === 'auto' ? t('conversions.tab.lead.autoInfo') : t('conversions.tab.lead.customInfo')}
            {' '}{t('conversions.tab.lead.contactHint')}
          </Text>
          {leadSelection === 'custom' && (
            <Button size="compact-xs" variant="light" color="brand" loading={leadSaving}
              onClick={() => saveLeads({ reset: true })}>{t('conversions.tab.lead.reset')}</Button>
          )}
        </Group>
      )}
      {leadError && <Text size="xs" c="red">{leadError}</Text>}
      {actions.length === 0 && (
        <Text size="sm" c="dimmed">{t('conversions.tab.empty')}</Text>
      )}
      {enabled.map(Row)}
      {others.length > 0 && <Text size="xs" c="dimmed" mt="xs">{t('conversions.tab.othersInactive')}</Text>}
      {others.map(Row)}
    </Stack>
  );
}
